import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Notifier from 'src/common/helpers/Notifier';
import { TextButton } from 'src/common/TailwindComponents';
import { useArchiveRecord, useUnarchiveRecord } from 'common/hooks';
import { useInvalidateQueries } from 'src/api/APIHooks';
import SpinIcon from 'src/common/spinners/SpinIcon';
import BulkAcceptInvoices from './BulkActions/BulkAcceptInvoices';
import BulkActionButton from '../../../common/utils/Workqueue/BulkActionButton';
import BulkExportInvoices from './BulkActions/BulkExportInvoices';
import BulkPayInvoices from './BulkActions/BulkPayInvoices';
import { InvoiceRejectionModal } from './BulkActions/InvoiceRejectionModal';
import BulkSubmitInvoices from './BulkActions/BulkSubmitInvoices';

const BulkActions = ({
  allInvoiceExportsLoading,
  bulkActions,
  className,
  currentEmployeeId,
  deselectAllExports,
  deselectInvoiceExports,
  deselectAllWorkqueueInvoices,
  deselectWorkqueueRow,
  downloadInvoiceExports,
  onClose,
  selectAll,
  selectedInvoiceExportIds,
  selectedRows,
}) => {
  const invalidateQueries = useInvalidateQueries();

  const deselectAllInvoices = () => {
    deselectAllExports();
    deselectAllWorkqueueInvoices();
  };

  const deselectInvoiceRows = (invoice) => {
    deselectInvoiceExports(invoice.id);
    deselectWorkqueueRow([invoice]);
  };

  const unarchiveInvoice = useUnarchiveRecord('invoice', {
      onSuccess: () => {
        onClose();
        deselectAllInvoices();
        invalidateQueries('invoice');
        Notifier.dispatch('success', 'Invoices unarchived.');
      },
  });

  const bulkUnarchiveInvoices = () => {
    selectedInvoiceExportIds.forEach((invoiceId) => unarchiveInvoice({ recordId: invoiceId }));
  };

  const archiveInvoice = useArchiveRecord('invoice', {
      onSuccess: () => {
        onClose();
        deselectAllInvoices();
        invalidateQueries('invoice');
        Notifier.dispatch('success', 'Invoices archived.');
      },
    });

  const bulkArchiveInvoices = () => {
    selectedInvoiceExportIds.forEach(
      (invoiceId) => archiveInvoice({ recordId: invoiceId, employeeId: currentEmployeeId }),
    );
  };

  return (
    <div className={cx(className, 'flex items-center space-x-4')}>
      <div className="flex divide-x divide-solid divide-dark-border-blue">
        <span className="pr-2">
          {allInvoiceExportsLoading ? (
            <SpinIcon />
          ) : (
            <div className="text-text-blue font-medium-font">
              {selectedInvoiceExportIds.length > 0 ? selectedInvoiceExportIds.length : 'None'}
              &nbsp;selected
            </div>
          )}
        </span>
        <TextButton onClick={selectAll} className="pr-2 pl-2">All</TextButton>
        <TextButton onClick={deselectAllInvoices} className="pl-2">None</TextButton>
      </div>
      <div className="flex space-x-4">
        {
          bulkActions.map((bulkAction) => {
            switch (bulkAction) {
              case 'Submit':
                return (
                  <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                    <BulkSubmitInvoices
                      deselectAll={deselectAllInvoices}
                      deselectRows={deselectInvoiceRows}
                      selectedRows={selectedRows}
                    />
                  </div>
                );

              case 'Accept':
                return (
                  <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                    <BulkAcceptInvoices
                      deselectAll={deselectAllInvoices}
                      deselectRows={deselectInvoiceRows}
                      selectedRows={selectedRows}
                      invoiceIds={selectedInvoiceExportIds}
                    />
                  </div>
                );

              case 'Pay':
                return (
                  <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                    <BulkPayInvoices
                      deselectAll={deselectAllInvoices}
                      deselectRows={deselectInvoiceRows}
                      selectedRows={selectedRows}
                      invoiceIds={selectedInvoiceExportIds}
                    />
                  </div>
                );

              case 'Reject':
                return (
                  <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                    <InvoiceRejectionModal
                      icon="IconCrossCircleV2"
                      invoice={selectedRows.length > 0 ? selectedRows[0] : {}}
                      disableInvoiceRejectionModal={selectedRows.length !== 1}
                      onSuccess={() => {
                        onClose();
                        deselectAllExports();
                        deselectAllWorkqueueInvoices();
                      }}
                    />
                  </div>
                );

              case 'Archive':
                // Bulk Acrchive Button
                return (
                  <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                    <BulkActionButton
                      action={bulkAction}
                      onClick={bulkArchiveInvoices}
                      icon="IconArchive"
                      disabled={selectedInvoiceExportIds.length === 0}
                    />
                  </div>
                );

              case 'Download':
                  // Invoice exports download button
                  return (
                    <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                      <BulkExportInvoices
                        action={bulkAction}
                        onClick={downloadInvoiceExports}
                        icon="IconDownloadV2"
                        disabled={selectedInvoiceExportIds.length === 0}
                      />
                    </div>

                  );

              case 'Unarchive':
                // Bulk Unarchive Button
                return (
                  <div className="cursor-pointer" key={`bulk-action-${bulkAction}-key`}>
                    <BulkActionButton
                      action={bulkAction}
                      onClick={bulkUnarchiveInvoices}
                      icon="IconUndo"
                      disabled={selectedInvoiceExportIds.length === 0}
                    />
                  </div>
                );

              default:
                return <></>;
            }
          })
        }
      </div>
    </div>
  );
};

BulkActions.propTypes = {
  allInvoiceExportsLoading: PropTypes.bool.isRequired,
  bulkActions: PropTypes.array.isRequired,
  className: PropTypes.string,
  deselectAllExports: PropTypes.func.isRequired,
  downloadInvoiceExports: PropTypes.func.isRequired,
  selectAll: PropTypes.func.isRequired,
  selectedInvoiceExportIds: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  deselectAllWorkqueueInvoices: PropTypes.func,
  currentEmployeeId: PropTypes.string,
  selectedRows: PropTypes.array.isRequired,
  deselectWorkqueueRow: PropTypes.func.isRequired,
  deselectInvoiceExports: PropTypes.func.isRequired,
};

BulkActions.defaultProps = {
  className: '',
  deselectAllWorkqueueInvoices: () => { },
  currentEmployeeId: '',
};

export default BulkActions;
